import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Trash2, AlertCircle, HardDrive } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

export default function BackupTab() {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAllData = async () => {
    if (deleteConfirmText !== 'DELETE ALL DATA') {
      toast.error('Please type "DELETE ALL DATA" exactly to confirm');
      return;
    }

    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/storage/delete-all-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All business data deleted successfully');
      setShowDeleteDialog(false);
      setDeleteConfirmText('');
    } catch (error) {
      toast.error('Failed to delete data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
        <div className="flex flex-col gap-6">
          {/* Info Banner */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 text-sm mb-1">Data Management</h3>
              <p className="text-sm text-blue-700">
                Use this section to permanently delete all business data. For backup and sync operations, use the <strong>DB Sync</strong> tab.
              </p>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="border-t border-slate-200 pt-6">
            <div className="flex items-start gap-3 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
              <Trash2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-red-900 text-sm mb-1">Danger Zone</h3>
                <p className="text-sm text-red-700 mb-3">
                  Delete all business data from the database. This action <strong>cannot be undone</strong>. User accounts will be preserved.
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  className="gap-2 bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete All Business Data
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete All Business Data?</DialogTitle>
            <DialogDescription>
              This will permanently delete all business data including expenses, employees, silica loads, and investments.
              User accounts will be preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-medium mb-2">⚠️ This action cannot be undone!</p>
              <p className="text-xs text-red-700">
                Type <strong>DELETE ALL DATA</strong> below to confirm
              </p>
            </div>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type: DELETE ALL DATA"
              className="font-mono"
            />
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAllData}
                disabled={deleting || deleteConfirmText !== 'DELETE ALL DATA'}
              >
                {deleting ? 'Deleting...' : 'Delete All Data'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
